/**
 * Execution Wizard
 *
 * Renders one section per WorkflowInput the pipeline declares (one per data-source
 * node on the canvas), typed exactly to that input's own input_type - there is no
 * generic scope picker and no way to provide anything the pipeline didn't declare.
 * Each section builds its own Scope; all of them are submitted together as
 * named_inputs to POST /workflows/trigger. Inputs a step reads as a single item
 * (decided by the agents' variables, see GET /workflows/{id}/driving-inputs) are unioned
 * by the backend into the batch that drives execution; inputs read only as all items /
 * schema stay reachable via scope.<name>.<field>. A successful trigger jumps straight to
 * the resulting execution monitor.
 */

import React, { useEffect, useState } from 'react';
import pipelineApi from '../api/client';
import type { Scope } from '../types/scope';
import './ExecutionWizard.css';

interface ExecutionWizardProps {
  pipelineId: string;
  pipelineName?: string;
  workflowInputs?: any[];
  onClose: () => void;
  onOpenExecution: (executionId: string) => void;
}

const INPUT_TYPE_LABELS: Record<string, string> = {
  item_id: 'Item',
  content_hash: 'Item (content hash)',
  scope_id: 'Scope',
  project_id: 'Project',
  tracker_id: 'Tracker',
  chapter_id: 'Chapter',
  tracker_schema: 'Tracker Schema',
  outline: 'Outline',
  tracker_list: 'Tracker List',
};

// How this input's resolved items participate in execution once submitted - purely
// informational here, since it follows from how the agents read each input.
const DRIVING_HINTS = {
  driving: 'Iterated over - per-item steps run once for each of these items.',
  context: 'Context only - steps read it as All items or Tracker schema, so it is not iterated over.',
};

// What each named input's ALM reference resolves to, matching how the backend interprets the
// resolved Scope in resolve_named_inputs/get_scope_source_ids.
const INPUT_TYPE_HINTS: Record<string, string> = {
  project_id: 'Every item in this project.',
  tracker_id: 'Every item in this tracker.',
  chapter_id: 'Every item under the given chapter(s)/folder(s), within their tracker.',
  tracker_schema: "This tracker's field definitions (names and choice options) - no items.",
  outline: 'The chapter structure (item summaries by layer) of a whole tracker, or under the given chapter(s) - no items.',
  tracker_list: 'The trackers (id and name) of this project - no items.',
};

// The ALM URL kind each input type is given as (a chapter is an issue in the ALM), and an example.
const ALM_REFERENCE_FORMATS: Record<string, { label: string; placeholder: string; multiple: boolean }> = {
  item_id: { label: 'Item URL(s) or ID(s)', placeholder: 'https://codebeamer.example.com/cb/issue/45341587\n45341588', multiple: true },
  content_hash: { label: 'Item URL(s) or ID(s)', placeholder: 'https://codebeamer.example.com/cb/issue/45341587\n45341588', multiple: true },
  chapter_id: { label: 'Chapter URL(s) or ID(s)', placeholder: 'https://codebeamer.example.com/cb/issue/45341500\n45341501', multiple: true },
  tracker_id: { label: 'Tracker URL or ID', placeholder: 'https://codebeamer.example.com/cb/tracker/12396', multiple: false },
  tracker_schema: { label: 'Tracker URL or ID', placeholder: 'https://codebeamer.example.com/cb/tracker/12396', multiple: false },
  project_id: { label: 'Project URL or ID', placeholder: 'https://codebeamer.example.com/cb/project/11', multiple: false },
  tracker_list: { label: 'Project URL or ID', placeholder: 'https://codebeamer.example.com/cb/project/11', multiple: false },
  outline: {
    label: 'Tracker URL, or chapter URL(s) or ID(s)',
    placeholder: 'https://codebeamer.example.com/cb/tracker/12396\nor https://codebeamer.example.com/cb/issue/45341500',
    multiple: true,
  },
};

const TRACKER_URL_PATTERN = /\/tracker\/\d+\/?$/i;

const ISSUE_URL_PATTERN = /\/issue\/(\d+)\/?$/i;
const EXTERNAL_ID_PATTERN = /^\d+$/;

function parseIdList(raw: string): string[] {
  return raw.split(/[\s,]+/).map(s => s.trim()).filter(Boolean);
}

function errorDetail(err: unknown, fallback: string): string {
  const detail = (err as any)?.response?.data?.detail;
  return detail || (err instanceof Error ? err.message : fallback);
}

// Resolves item references (issue URLs or bare ALM item ids) to internal item UUIDs in one
// /workflows/resolve-source-items call for the whole batch.
async function resolveItemReferences(rawEntries: string[]): Promise<string[]> {
  const sourceItems: string[] = [];
  const invalid: string[] = [];
  for (const raw of rawEntries) {
    const urlMatch = raw.match(ISSUE_URL_PATTERN);
    if (urlMatch) sourceItems.push(urlMatch[1]);
    else if (EXTERNAL_ID_PATTERN.test(raw)) sourceItems.push(raw);
    else invalid.push(raw);
  }
  if (invalid.length) throw new Error(`Not an item URL or ID: ${invalid.join(', ')}`);

  const unique = Array.from(new Set(sourceItems));
  const { resolved, ambiguous, not_found } = await pipelineApi.resolveSourceItems(unique);
  const problems: string[] = [];
  if (not_found.length) problems.push(`not found: ${not_found.join(', ')}`);
  if (Object.keys(ambiguous).length) problems.push(`ambiguous, exists in multiple scopes: ${Object.keys(ambiguous).join(', ')}`);
  if (problems.length) throw new Error(`Could not resolve: ${problems.join('; ')}`);
  return unique.map(id => resolved[id]);
}

// Resolves one tracker/project/chapter URL or bare ALM id to the Scope it names.
async function resolveAlmReference(raw: string, inputType: string): Promise<Scope> {
  const result = await pipelineApi.resolveAlmUrl(raw, inputType);
  if (result.not_found || result.scope === null) throw new Error(`Could not resolve ${raw}.`);
  return result.scope;
}

type BuildResult = { kind: 'empty' } | { kind: 'error'; message: string } | { kind: 'scope'; scope: Scope };

// Each chapter resolves on its own; ScopeFromChapter takes several, but only within one tracker.
async function buildChapterScope(entries: string[], inputType: string): Promise<BuildResult> {
  const chapters = await Promise.all(entries.map(entry => resolveAlmReference(entry, inputType)));
  const first = chapters[0];
  if (first.type_id !== 'ScopeFromChapter' || chapters.some(c => c.type_id !== 'ScopeFromChapter')) {
    return { kind: 'error', message: 'Unexpected scope type returned for a chapter.' };
  }
  const sameTracker = chapters.every(
    c => c.type_id === 'ScopeFromChapter' && c.scope_id === first.scope_id && c.scope_project_id === first.scope_project_id && c.scope_type_id === first.scope_type_id,
  );
  if (!sameTracker) return { kind: 'error', message: 'All chapters must be in the same tracker.' };
  return {
    kind: 'scope',
    scope: { ...first, chapter_ids: chapters.flatMap(c => (c.type_id === 'ScopeFromChapter' ? c.chapter_ids : [])) },
  };
}

async function buildScopeForInput(inputType: string, raw: string): Promise<BuildResult> {
  const entries = parseIdList(raw);
  if (entries.length === 0) return { kind: 'empty' };
  try {
    switch (inputType) {
      case 'item_id':
      case 'content_hash':
        return { kind: 'scope', scope: { type_id: 'ScopeFromSourceIds', source_ids: await resolveItemReferences(entries) } };
      case 'tracker_id':
      case 'tracker_schema':
      case 'project_id':
      case 'tracker_list':
        if (entries.length > 1) return { kind: 'error', message: 'Only one URL or ID is allowed.' };
        return { kind: 'scope', scope: await resolveAlmReference(entries[0], inputType) };
      case 'outline':
        // A tracker URL outlines the whole tracker; otherwise the entries are chapters (a bare id is a chapter).
        if (entries.some(entry => TRACKER_URL_PATTERN.test(entry))) {
          if (entries.length > 1) return { kind: 'error', message: 'Give either one tracker URL or chapter URL(s), not both.' };
          return { kind: 'scope', scope: await resolveAlmReference(entries[0], inputType) };
        }
        return await buildChapterScope(entries, inputType);
      case 'chapter_id':
        return await buildChapterScope(entries, inputType);
      default:
        return { kind: 'error', message: `Unsupported input type: ${inputType} - use a tracker, project, chapter or item input instead.` };
    }
  } catch (err) {
    return { kind: 'error', message: errorDetail(err, 'Failed to resolve.') };
  }
}

// The single ALM reference field for one declared input: its URL(s) or external ID(s), never internal ids.
const InputTypeFields: React.FC<{
  inputType: string;
  value: string;
  onChange: (value: string) => void;
}> = ({ inputType, value, onChange }) => {
  const format = ALM_REFERENCE_FORMATS[inputType];
  if (!format) return <p className="config-error">Unsupported input type: {inputType}</p>;

  return (
    <div className="config-field">
      <label className="config-label">{format.label}</label>
      <p className="config-description">
        Paste the {format.multiple ? 'URLs from your ALM (comma or newline separated)' : 'URL from your ALM'}, or just the
        {format.multiple ? ' numeric IDs' : ' numeric ID'} at its end.
      </p>
      {format.multiple ? (
        <textarea className="config-input" rows={3} value={value} onChange={e => onChange(e.target.value)} placeholder={format.placeholder} />
      ) : (
        <input className="config-input" type="text" value={value} onChange={e => onChange(e.target.value)} placeholder={format.placeholder} />
      )}
    </div>
  );
};

export const ExecutionWizard: React.FC<ExecutionWizardProps> = ({
  pipelineId,
  pipelineName,
  workflowInputs,
  onClose,
  onOpenExecution,
}) => {
  // Inputs with a value_source are filled in from an upstream step's output at run time -
  // they must not be sent in named_inputs, so they're listed read-only instead.
  const inputs = (workflowInputs || []).filter((input: any) => !input.value_source);
  const agentFedInputs = (workflowInputs || []).filter((input: any) => input.value_source);
  const [rawByName, setRawByName] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [drivingInputNames, setDrivingInputNames] = useState<Set<string> | null>(null);

  // Reflects the last save - the trigger derives the same set from the saved workflow.
  useEffect(() => {
    pipelineApi
      .getDrivingInputs(pipelineId)
      .then((names) => setDrivingInputNames(new Set(names)))
      .catch(() => setDrivingInputNames(null));
  }, [pipelineId]);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const namedInputs: Record<string, Scope> = {};
      const problems: string[] = [];

      for (const input of inputs) {
        const result = await buildScopeForInput(input.input_type, rawByName[input.name] ?? '');
        if (result.kind === 'scope') {
          namedInputs[input.name] = result.scope;
        } else if (result.kind === 'error') {
          problems.push(`${input.name}: ${result.message}`);
        } else if (input.required !== false) {
          problems.push(`${input.name}: required.`);
        }
      }

      if (problems.length > 0) {
        setError(problems.join(' '));
        return;
      }

      const result = await pipelineApi.executePipeline(pipelineId, namedInputs);
      onOpenExecution(result.completion_barrier_id);
    } catch (err) {
      // The backend returns 404 when no iterated input resolved to any items, which
      // surfaces here as an axios error with a `detail` message in the body.
      const detail = (err as any)?.response?.data?.detail;
      setError('Failed to trigger workflow: ' + (detail || (err instanceof Error ? err.message : 'Unknown error')));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="execution-wizard">
      <div className="sidebar-header">
        <h3>Execute{pipelineName ? `: ${pipelineName}` : ' Pipeline'}</h3>
        <button className="close-button" onClick={onClose}>×</button>
      </div>

      <div className="sidebar-body">
        {inputs.length === 0 && agentFedInputs.length === 0 && (
          <p className="config-description">
            This workflow declares no inputs - add a data source node on the canvas first.
          </p>
        )}

        {inputs.map((input: any) => (
          <div className="config-section" key={input.name}>
            <h4>
              {INPUT_TYPE_LABELS[input.input_type] || input.input_type}: {input.name}{' '}
              {input.required !== false && <span className="required">*</span>}
            </h4>
            {input.description && <p className="config-description">{input.description}</p>}
            {INPUT_TYPE_HINTS[input.input_type] && (
              <p className="config-description">{INPUT_TYPE_HINTS[input.input_type]}</p>
            )}
            {drivingInputNames && input.input_type !== 'tracker_schema' && input.input_type !== 'outline' && input.input_type !== 'tracker_list' && (
              <p className="config-description">
                {drivingInputNames.has(input.name) ? DRIVING_HINTS.driving : DRIVING_HINTS.context}
              </p>
            )}
            <div className="config-fields">
              <InputTypeFields
                inputType={input.input_type}
                value={rawByName[input.name] ?? ''}
                onChange={value => setRawByName(prev => ({ ...prev, [input.name]: value }))}
              />
            </div>
          </div>
        ))}

        {agentFedInputs.length > 0 && (
          <div className="config-section">
            <h4>Filled in by agents</h4>
            {agentFedInputs.map((input: any) => (
              <p className="config-description" key={input.name}>
                {input.name}: filled in by {input.value_source.upstream_step_reference} at run time
              </p>
            ))}
          </div>
        )}

        {error && <div className="config-error">{error}</div>}
      </div>

      <div className="sidebar-footer">
        <button className="button-secondary" onClick={onClose} disabled={submitting}>
          Cancel
        </button>
        <button className="button-primary" onClick={handleSubmit} disabled={submitting}>
          {submitting ? 'Triggering...' : 'Trigger Workflow'}
        </button>
      </div>
    </div>
  );
};
